import React, { memo, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';

import { intersectionObserver } from '../../../../utils/intersection-observer';
import { styles } from './ProductOverviewContent.css';

type ProductOverviewContentProps = { content: string };

const ProductOverviewContent = memo(({ content }: ProductOverviewContentProps): JSX.Element => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const contentParts = content.split('<iframe');
  let contentHtml = null;

  if (contentParts.length >= 1) {
    // This is some legacy parsing of content to wrap iframes and apply iframe lazy loading
    let parsedHtml = '';
    contentParts.forEach((part: string) => {
      if (part.includes('</iframe>') && (part.includes('youtube.com/embed') || part.includes('player.vimeo'))) {
        const subParts = part.split('</iframe>');
        const wrappedIframe = `<div class="rte__video-wrapper"><iframe class="lazyload" data-lazyload-iframe ${subParts[0].replace(
          'src=',
          'data-src='
        )}</iframe></div>`;
        parsedHtml += wrappedIframe;
        parsedHtml += subParts[1];
      } else {
        parsedHtml += part;
      }
    });
    contentHtml = parsedHtml;
  } else {
    contentHtml = content;
  }

  const loadVideoIframe = entry => {
    if (entry.intersectionRatio < 0.5) return;
    entry.target.src = entry.target.dataset.src;
  };

  useEffect(() => {
    if (!containerRef.current) return;
    const videoIframes = containerRef.current.querySelectorAll('[data-lazyload-iframe]');
    if (videoIframes.length === 0) return;
    intersectionObserver({
      targets: videoIframes,
      callback: loadVideoIframe,
      options: {
        rootMargin: '0px',
        threshold: 0.5,
      },
      once: true,
    });
  }, []);

  // Sanitize content HTML
  const sanitizedHtml = `${DOMPurify.sanitize(contentHtml, {
    ADD_TAGS: ['iframe'],
    ADD_ATTR: ['allowfullscreen'],
  })}<div class="${styles.spacer}"></div>`;

  return <div className={styles.root} ref={containerRef} dangerouslySetInnerHTML={{ __html: sanitizedHtml }}></div>;
});

ProductOverviewContent.displayName = 'ProductOverviewContent';

export default ProductOverviewContent;
